import { Plus, Trash, CheckCircle, Circle } from 'lucide-react';
import Button from '../../../../components/Button';
import type { CommunityQuestionOption } from '../../types/community-questions';

interface QuestionOptionsManagerProps {
  options: Record<string, CommunityQuestionOption>;
  optionsOrder: string[];
  type: 'question' | 'poll';
  onChange: (field: string, value: Record<string, CommunityQuestionOption> | string[]) => void;
}

export const QuestionOptionsManager = ({ options, optionsOrder, type, onChange }: QuestionOptionsManagerProps) => {
  const handleAddOption = () => {
    const newOptionId = String(optionsOrder.length + 1);
    const newOptions = {
      ...options,
      [newOptionId]: {
        label: '',
        responseCount: 0,
        isCorrect: false,
      },
    };
    const newOrder = [...optionsOrder, newOptionId];

    onChange('options', newOptions);
    onChange('optionsOrder', newOrder);
  };

  const handleRemoveOption = (optionId: string) => {
    if (optionsOrder.length <= 2) {
      alert('You must have at least 2 options');
      return;
    }

    const newOptions = { ...options };
    delete newOptions[optionId];
    const newOrder = optionsOrder.filter(id => id !== optionId);

    onChange('options', newOptions);
    onChange('optionsOrder', newOrder);
  };

  const handleOptionLabelChange = (optionId: string, label: string) => {
    const newOptions = {
      ...options,
      [optionId]: {
        ...options[optionId],
        label,
      },
    };
    onChange('options', newOptions);
  };

  const handleToggleCorrect = (optionId: string) => {
    const newOptions = {
      ...options,
      [optionId]: {
        ...options[optionId],
        isCorrect: !options[optionId].isCorrect,
      },
    };
    onChange('options', newOptions);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Options * (minimum 2)
      </label>

      <div className="space-y-2">
        {optionsOrder.map((optionId) => {
          const option = options[optionId];
          if (!option) return null;

          return (
            <div key={optionId} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start gap-3">
                {/* Option Number */}
                <div className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-medium text-gray-700">{optionId}</span>
                </div>

                {/* Option Content */}
                <div className="flex-1 min-w-0 space-y-2">
                  <textarea
                    value={option.label}
                    onChange={(e) => handleOptionLabelChange(optionId, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter option text"
                    rows={1}
                    required
                  />

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* Correct Answer Toggle (only for questions) */}
                    {type === 'question' && (
                      <Button
                        type="button"
                        onClick={() => handleToggleCorrect(optionId)}
                        size="sm"
                        className={`${option.isCorrect
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                          }`}
                        icon={option.isCorrect ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                      >
                        {option.isCorrect ? 'Correct Answer' : 'Mark as Correct'}
                      </Button>
                    )}

                    {/* Response Count Display */}
                    <span className="text-xs text-gray-500">
                      {option.responseCount} response{option.responseCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Delete Button */}
                <Button
                  type="button"
                  onClick={() => handleRemoveOption(optionId)}
                  variant="ghost"
                  size="sm"
                  icon={<Trash className="w-4 h-4 text-red-600" />}
                  title="Remove option"
                  className="text-red-600 hover:bg-red-50"
                />
              </div>
            </div>
          );
        })}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleAddOption}
            variant="secondary"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
          >
            Add Option
          </Button>
        </div>
      </div>

      {optionsOrder.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No options yet. Click "Add Option" to create one.
        </p>
      )}
    </div>
  );
};
